import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Bar,
  Brush,
  Legend,
} from "recharts";
import "./SplineChart.css";


const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "#2a2a2f",
          padding: "8px 12px",
          borderRadius: "6px",
        }}
      >
        <p style={{ color: "#84cc16", margin: 0 }}>{label}</p>
        {payload
          .filter((item) => item.dataKey !== "emissionsBar")
          .map((item, index) => (
            <p
              key={index}
              style={{
                margin: 0,
                color: item.stroke || "#fff",
              }}
            >
              {item.name || item.dataKey}: {item.value}
            </p>
          ))}
      </div>
    );
  }
  return null;
};

export default function SplineChart({ data }) {
  const processedData = data.map((d) => ({
    ...d,
    emissionsBar: d.emissions, 
  }));

  return (
    <div className="spline-container">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={processedData}
          margin={{ top: 10, right: 40, left: 40, bottom: 20 }}
        >
         
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />

      
          <XAxis
            dataKey="time"
            stroke="#aaa"
            tickFormatter={(t) => {
              const [month, day, hour] = t.split(" ");
              return hour === "0:00" ? `${month} ${day}` : "";
            }}
            interval={0}
            minTickGap={50}
          />

         
          <YAxis
            yAxisId="left"
            label={{
              value: "Tons (100)",
              angle: -90,
              position: "insideLeft",
              offset: 10,
              style: { fill: "#ffffffb0", fontSize: 12, fontWeight: 700 },
            }}
            stroke="#aaa"
          />

        
          <YAxis
            yAxisId="right"
            orientation="right"
            label={{
              value: "Miles per hour",
              angle: -90,
              position: "insideRight",
              offset: 10,
              style: { fill: "#ffffffb0", fontSize: 12, fontWeight: 700 },
            }}
            stroke="#aaa"
          />

         
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            align="left"
            iconType="circle"
            content={({ payload }) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "18px",
                  padding: "0 20px 2px 20px", 
                  marginBottom: "20px",
                  lineHeight: 1.1,
                }}
              >
                {payload
                  ?.filter((entry) => entry.value !== "emissionsBar")
                  .map((entry, index) => (
                    <div
                      key={`item-${index}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#fff",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: entry.color,
                          display: "inline-block",
                          marginRight: 6,
                        }}
                      ></span>
                      {entry.value}
                    </div>
                  ))}
              </div>
            )}
          />

          
          <Bar
            yAxisId="left"
            dataKey="emissionsBar"
            fill="url(#barGradient)"
            barSize={3}
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
            legendType="none"
          />

          
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="accidents"
            stroke="#ffffff"
            dot={false}
            activeDot={{ r: 6, fill: "#ffffff" }}
            strokeWidth={2}
            strokeDasharray="5 2"
            name="Others"
          />

          <Line
            yAxisId="left"
            type="monotone"
            dataKey="emissions"
            stroke="#84cc16"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6, fill: "#84cc16" }}
            strokeDasharray="5 2"
            name="Emissions"
          />

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="bus"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, fill: "#3b82f6" }}
            strokeDasharray="5 2"
            name="Bus"
          />

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="truck"
            stroke="#f97316"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, fill: "#f97316" }}
            strokeDasharray="5 2"
            name="Truck"
          />

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="car"
            stroke="#ee0000ff"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, fill: "#ee0000ff" }}
            strokeDasharray="5 2"
            name="Car"
          />

          
          <Brush dataKey="time" height={20} stroke="#84cc16" travellerWidth={10} />

         
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#84cc16" stopOpacity={0.7} />
              <stop offset="100%" stopColor="#84cc16" stopOpacity={0.1} />
            </linearGradient>
          </defs>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
